// ── Geographic Service ──
// Orchestration layer for geographic calculations
// Base utilities remain in utils/geo.ts

import {
  Coords,
  TravelEstimate,
  haversineDistanceKm,
  estimateTravel,
  formatTravelEstimate,
} from "../utils/geo";

export type { Coords, TravelEstimate };

// ── Core Methods (re-exported from utils/geo.ts) ──

export { haversineDistanceKm, estimateTravel, formatTravelEstimate };

// ── Proximity Sorting ──

export interface WithDistance<T> {
  item: T;
  distance: number;
}

/**
 * Sort items by distance from an origin point.
 * Items without coordinates are placed at the end with distance = Infinity.
 */
export function sortByProximity<
  T extends { lat?: number | null; lng?: number | null },
>(items: T[], origin: Coords): WithDistance<T>[] {
  const itemsWithDistance = items.map((item) => {
    const hasCoords = item.lat != null && item.lng != null;
    const distance = hasCoords
      ? haversineDistanceKm(origin, { lat: item.lat!, lng: item.lng! })
      : Infinity;
    return { item, distance };
  });

  return itemsWithDistance.sort((a, b) => a.distance - b.distance);
}

/**
 * Find items within a given radius from an origin point.
 */
export function findWithinRadius<
  T extends { lat?: number | null; lng?: number | null },
>(items: T[], origin: Coords, radiusKm: number): WithDistance<T>[] {
  return sortByProximity(items, origin).filter((wd) => wd.distance <= radiusKm);
}

// ── Gap Detection ──

export interface GapResult {
  index: number; // Insert after this index
  distance: number;
  fromCoords: Coords;
  toCoords: Coords;
}

/**
 * Find the largest geographic gap in an ordered sequence of items.
 * Useful for identifying where accommodation transitions should occur.
 * Returns null if fewer than 2 items have coordinates.
 */
export function findLargestGap<
  T extends { lat?: number | null; lng?: number | null },
>(items: T[]): GapResult | null {
  // Filter to items with coordinates
  const withCoords = items
    .map((item, idx) => ({ item, originalIndex: idx }))
    .filter(({ item }) => item.lat != null && item.lng != null);

  if (withCoords.length < 2) return null;

  let maxGap: GapResult | null = null;

  for (let i = 0; i < withCoords.length - 1; i++) {
    const from = withCoords[i];
    const to = withCoords[i + 1];
    const fromCoords: Coords = { lat: from.item.lat!, lng: from.item.lng! };
    const toCoords: Coords = { lat: to.item.lat!, lng: to.item.lng! };
    const distance = haversineDistanceKm(fromCoords, toCoords);

    if (!maxGap || distance > maxGap.distance) {
      maxGap = {
        index: from.originalIndex,
        distance,
        fromCoords,
        toCoords,
      };
    }
  }

  return maxGap;
}

// ── Clustering ──

/**
 * Group items into clusters based on proximity.
 * Uses a simple single-linkage approach: items within `thresholdKm` of any cluster member join that cluster.
 */
export function clusterByProximity<
  T extends { lat?: number | null; lng?: number | null },
>(items: T[], thresholdKm: number): T[][] {
  const hasCoords = (item: T): item is T & { lat: number; lng: number } =>
    item.lat != null && item.lng != null;

  const withCoords = items.filter(hasCoords);
  const withoutCoords = items.filter((item) => !hasCoords(item));

  const clusters: T[][] = [];
  const assigned = new Set<number>();

  for (let i = 0; i < withCoords.length; i++) {
    if (assigned.has(i)) continue;

    const cluster: T[] = [withCoords[i]];
    assigned.add(i);

    // Find all items within threshold of any cluster member
    let changed = true;
    while (changed) {
      changed = false;
      for (let j = 0; j < withCoords.length; j++) {
        if (assigned.has(j)) continue;
        const candidate = withCoords[j];
        const candidateCoords: Coords = {
          lat: candidate.lat,
          lng: candidate.lng,
        };

        for (const member of cluster) {
          const memberCoords: Coords = { lat: member.lat!, lng: member.lng! };
          if (
            haversineDistanceKm(memberCoords, candidateCoords) <= thresholdKm
          ) {
            cluster.push(candidate);
            assigned.add(j);
            changed = true;
            break;
          }
        }
      }
    }

    clusters.push(cluster);
  }

  // Items without coords go in their own singleton clusters
  for (const item of withoutCoords) {
    clusters.push([item]);
  }

  return clusters;
}

// ── Service Object ──
// Convenience export for dependency injection / mocking

export const geoService = {
  haversineDistanceKm,
  estimateTravel,
  formatTravelEstimate,
  sortByProximity,
  findWithinRadius,
  findLargestGap,
  clusterByProximity,
};
